export enum PlaceType {
  Unknown = 0,
  Airport = 1,
  City = 2,
  Neighborhood = 3,
  Station = 4,
  PlaceOfInterest = 5,
  Address = 6
}

export class Location {

  constructor(
    locationId?: string,
    locationName?: string,
    type?: string,
    shortName?: string
  ) {
    const placeType = <PlaceType>Number(type);
    this.locationId = locationId;
    this.locationName = locationName;
    this.type = isNaN(placeType) ? null : placeType;
    this.shortName = shortName;
  }

  locationId?: string;
  locationName?: string;
  type?: PlaceType;
  shortName?: string;

  hasAddress() {
    return this.type === PlaceType.Address && !!this.locationName;
  }

}

export class Time {

  constructor(hour?: number, minute?: number) {
    this.hour = (typeof hour === 'number') ? hour : 12;
    this.minute = minute || 0;
  }

  hour: number;
  minute: number;

  lessThan(time: Time): boolean {
    return this.toString() < time.toString();
  }

  toString(): string {
    return this.hour.toString().padStart(2, '0') + this.minute.toString().padStart(2, '0');
  }

  toMs(): number {
    return this.hour * 60 * 60 * 1000 + this.minute * 60 * 1000;
  }

  fill(tm: string): Time {
    if (!tm) {
      return null;
    }

    const hrs = parseInt(tm.substr(0, 2), 10);
    const mnt = parseInt(tm.substr(2, 2), 10);
    this.hour = hrs;
    this.minute = mnt;

    return this;
  }

  get time(): Date {
    // be aware, only hours and minutes are valid in this Date instance
    return new Date(2000, 1, 1, this.hour, this.minute);
  }
}

export interface SearchCriteriaData {
  pickUp: {
    location: Location;
    time: Time;
    date: Date;
  };

  dropOff: {
    location: Location;
    time: Time;
    date: Date;
  };

  timestamp: number;

  fill(scp: SearchCriteriaPlain): SearchCriteriaData;
  hasDropOffLocation(): boolean;
  isAirport(): boolean;
}

export class SearchCriteria implements SearchCriteriaData {

  constructor(
    pickUpLocation?: Location, pickUpDate?: Date, pickUpTime?: Time,
    dropOffLocation?: Location, dropOffDate?: Date, dropOffTime?: Time
  ) {

    this.timestamp = new Date().getTime();

    this.pickUp = {
      location: pickUpLocation,
      time: pickUpTime,
      date: pickUpDate
    };

    this.dropOff = {
      location: dropOffLocation,
      time: dropOffTime,
      date: dropOffDate
    };

  }

  pickUp: {
    location: Location;
    time: Time;
    date: Date;
  };

  dropOff: {
    location: Location;
    time: Time;
    date: Date;
  };

  timestamp: number;

  fill(scp: SearchCriteriaPlain): SearchCriteria {

    if (scp.timestamp) {
      this.timestamp = scp.timestamp;
    }

    if (scp.hasPickUpLocation()) {
      this.pickUp.location =
        new Location(
          scp.pickUp_location_id,
          scp.pickUp_location_name,
          scp.pickUp_location_type);
    }

    if (!!scp.pickUp_date) {
      this.pickUp.date = stringToDate(scp.pickUp_date);
    }

    if (!!scp.pickUp_time) {
      this.pickUp.time = new Time().fill(scp.pickUp_time);
    }

    if (scp.hasDropOffLocation()) {
      this.dropOff.location =
        new Location(
          scp.dropOff_location_id,
          scp.dropOff_location_name,
          scp.dropOff_location_type);
    }

    if (!!scp.dropOff_date) {
      this.dropOff.date = stringToDate(scp.dropOff_date);
    }

    if (!!scp.dropOff_time) {
      this.dropOff.time = new Time().fill(scp.dropOff_time);
    }

    return this;
  }

  hasDropOffLocation(): boolean {
    if (!this.dropOff) return false;
    if (!this.dropOff.location) return false;
    if (!this.dropOff.location.type) return false;
    if (!this.dropOff.location.locationId || !this.dropOff.location.locationName) return false;
    return true;
  }

  isAirport() {
    return this.pickUp
      && this.pickUp.location
      && this.pickUp.location.type === PlaceType.Airport;
  }
}

export class SearchCriteriaPlain {

  pickUp_location_id: string;
  pickUp_location_name: string;
  pickUp_location_type: string;
  pickUp_time: string;
  pickUp_date: string;

  dropOff_location_id: string;
  dropOff_location_name: string;
  dropOff_location_type: string;
  dropOff_time: string;
  dropOff_date: string;

  timestamp: number;

  hasDropOffLocation(): boolean {

    const locTypeIsAddress = !!this.dropOff_location_type
      && PlaceType[this.dropOff_location_type] === PlaceType[PlaceType.Address];

    return (locTypeIsAddress && !!this.dropOff_location_name) ||
      !!(this.dropOff_location_id &&
      this.dropOff_location_name &&
      this.dropOff_location_type);
  }

  hasPickUpLocation(): boolean {

    const locTypeIsAddress = !!this.pickUp_location_type
      && PlaceType[this.pickUp_location_type] === PlaceType[PlaceType.Address];

    return (locTypeIsAddress && !!this.pickUp_location_name) ||
      !!(this.pickUp_location_id &&
        this.pickUp_location_name &&
        this.pickUp_location_type);
  }

  checkOneWayRestriction(): boolean {

    const hasDropOffLocation = this.hasDropOffLocation();
    const isAirport = !!this.pickUp_location_type
      && PlaceType[this.pickUp_location_type] === PlaceType[PlaceType.Airport];

    return isAirport || !hasDropOffLocation;
  }

  isValid(): boolean {

    const hasPickUpLocation = this.hasPickUpLocation();

    const passedOneWayRestriction = this.checkOneWayRestriction();

    const hasDatesAndTimes = !!(this.pickUp_time &&
      this.pickUp_date &&
      this.dropOff_time &&
      this.dropOff_date);

    const validDates =
      !!stringToDate(this.pickUp_date) && !!stringToDate(this.dropOff_date);

    const timeRegex = /^[0-9]{4}$/g;
    let validTimes = timeRegex.test(this.pickUp_time);
    timeRegex.lastIndex = 0;
    validTimes = validTimes && timeRegex.test(this.dropOff_time);

    return hasPickUpLocation && passedOneWayRestriction &&
      hasDatesAndTimes && validDates && validTimes;
  }

  fill(sc: SearchCriteria): SearchCriteriaPlain {

    if (sc.timestamp) {
      this.timestamp = sc.timestamp;
    }

    this.pickUp_location_id = sc.pickUp.location.locationId;
    this.pickUp_location_name = sc.pickUp.location.locationName;
    this.pickUp_location_type = (<PlaceType>sc.pickUp.location.type).toString();

    this.pickUp_time = sc.pickUp.time.toString();
    this.pickUp_date = this.convertDateToString(sc.pickUp.date);

    if (sc.hasDropOffLocation()) {
      this.dropOff_location_id = sc.dropOff.location.locationId;
      this.dropOff_location_name = sc.dropOff.location.locationName;
      this.dropOff_location_type = sc.dropOff.location.type.toString();
    }

    this.dropOff_time = sc.dropOff.time.toString();
    this.dropOff_date = this.convertDateToString(sc.dropOff.date);

    return this;
  }

  private convertDateToString(date: Date) {

    if (typeof date === "string") {
      date = new Date(date);
    }

    const year = date.getFullYear();
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const day = date.getDate().toString().padStart(2, '0');

    return `${year}-${month}-${day}`;
  }

}

export class SearchFormConfig {
  footer: {
    text: string;
  };
  button: {
    text: string;
  };
  behavior: {
    redirectInsteadSearch: boolean;
    isPopupView: boolean;
  };
}

export function stringToDate(stringDate: string): Date {

  // YYYY-MM-DD
  // parse it in this way to avoid timezone issues

  const valid = /^\d{4}-\d{2}-\d{2}$/g.test(stringDate);

  if (!valid) {
    return null;
  }

  const data = stringDate.split('-');

  const year = Number(data[0]);
  const month = Number(data[1]);
  const day = Number(data[2]);

  return new Date(year, month - 1, day);
}
